import { useState, useEffect, useRef } from "react";
import {
  HiOutlineCalendar, HiOutlinePaperAirplane,
  HiOutlineChatBubbleLeftRight, HiOutlineEnvelope,
  HiOutlineArrowPath, HiOutlinePaperClip,
  HiOutlineXMark, HiOutlineCheckCircle,
  HiOutlineDocumentText,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./NotificationsPage.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "";
const fmtShort = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric" }) : "";

/* ── Reply Composer ── */
function ReplyComposer({ noteId, onSent, onCancel }) {
  const [text,    setText]    = useState("");
  const [file,    setFile]    = useState(null);
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState("");
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError("File must be under 5 MB."); return; }
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf","jpg","jpeg","png"].includes(ext)) { setError("Only PDF, JPG, and PNG files are allowed."); return; }
    setFile(f); setError("");
  };

  const handleSend = async () => {
    if (!text.trim() && !file) { setError("Please write a reply or attach a file."); return; }
    setSending(true); setError("");
    try {
      await portalAPI.replyToNote(noteId, text.trim(), file);
      onSent({ reply_text: text.trim(), file_name: file?.name, created_at: new Date().toISOString() });
      setText(""); setFile(null);
    } catch (err) { setError(err.message || "Failed to send. Please try again."); }
    finally { setSending(false); }
  };

  return (
    <div className="np__reply-composer">
      <textarea
        className="np__reply-input"
        rows={4}
        placeholder="Write your reply here…"
        value={text}
        onChange={e => setText(e.target.value)}
      />

      {/* File attachment */}
      <div className="np__reply-attach-row">
        <button type="button" className="np__attach-btn" onClick={() => fileRef.current?.click()}>
          <HiOutlinePaperClip /> {file ? "Change file" : "Attach file"}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
          style={{ display:"none" }} onChange={handleFile}/>
        {file && (
          <div className="np__file-chip">
            <HiOutlineDocumentText/>
            <span>{file.name}</span>
            <button type="button" onClick={() => setFile(null)}><HiOutlineXMark /></button>
          </div>
        )}
        <p className="np__attach-hint">PDF, JPG, PNG — max 5 MB</p>
      </div>

      {error && <p className="np__reply-error">{error}</p>}

      <div className="np__reply-actions">
        <button type="button" className="p-btn p-btn--outline" onClick={onCancel} disabled={sending}>
          Cancel
        </button>
        <button type="button" className="p-btn p-btn--fill" onClick={handleSend}
          disabled={sending || (!text.trim() && !file)}>
          <HiOutlinePaperAirplane/>
          {sending ? "Sending…" : "Send Reply"}
        </button>
      </div>
    </div>
  );
}

/* ── Single note card ── */
function NoteCard({ note }) {
  const [showReply, setShowReply] = useState(false);
  const [replies,   setReplies]   = useState(note.replies || []);
  const [sent,      setSent]      = useState(false);

  const handleSent = (reply) => {
    setReplies(prev => [...prev, reply]);
    setShowReply(false);
    setSent(true);
  };

  return (
    <div className="np__note-card">
      {/* Admin note */}
      <div className="np__note-header">
        <div className="np__note-avatar">
          <HiOutlineEnvelope />
        </div>
        <div className="np__note-meta-block">
          <p className="np__note-from">Forge Team</p>
          <p className="np__note-time">{fmt(note.created_at)}</p>
        </div>
        <span className="np__note-badge">From Admin</span>
      </div>

      <div className="np__note-body">
        <p className="np__note-text">{note.note}</p>
      </div>

      {/* Previous replies */}
      {replies.length > 0 && (
        <div className="np__replies">
          {replies.map((r, i) => (
            <div key={r.id || i} className="np__reply">
              <div className="np__reply-avatar">You</div>
              <div className="np__reply-body">
                <div className="np__reply-meta">
                  <span className="np__reply-name">You</span>
                  <span className="np__reply-time">{fmtShort(r.created_at)}</span>
                </div>
                {r.reply_text && <p className="np__reply-text">{r.reply_text}</p>}
                {r.file_name && (
                  <div className="np__reply-file">
                    <HiOutlineDocumentText /> {r.file_name}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply sent confirmation */}
      {sent && (
        <div className="np__reply-sent">
          <HiOutlineCheckCircle /> Reply sent — our team will see it in the admin panel.
        </div>
      )}

      {/* Reply composer toggle */}
      {!showReply && (
        <button type="button" className="np__reply-toggle" onClick={() => { setShowReply(true); setSent(false); }}>
          <HiOutlineChatBubbleLeftRight/>
          Reply to this note
        </button>
      )}
      {showReply && (
        <ReplyComposer
          noteId={note.id}
          onSent={handleSent}
          onCancel={() => setShowReply(false)}
        />
      )}
    </div>
  );
}

/* ── Main page ── */
export default function NotificationsPage() {
  const [notes,    setNotes]   = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState("");

  const fetchNotes = async () => {
    setLoading(true); setError("");
    try {
      const res = await portalAPI.application();
      setNotes(res.data?.applicant_notes || []);
    } catch (err) { setError("Could not load messages. Please try again."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchNotes(); }, []);

  return (
    <PortalLayout
      eyebrow="Messages"
      pageTitle="Messages from the Team"
      quote="Well done is better than well said."
      quoteAuthor="Benjamin Franklin"
    >
      <p className="np__sub">
        Messages from the Forge team about your application appear here.
        You can reply directly — your response goes straight to our admin team.
      </p>

      {loading ? (
        <div className="np__loading">
          <div className="np__spinner"/>
          <p>Loading messages…</p>
        </div>
      ) : error ? (
        <div className="pc np__error-state">
          <p>{error}</p>
          <button type="button" className="p-btn p-btn--outline" onClick={fetchNotes}>
            <HiOutlineArrowPath /> Try again
          </button>
        </div>
      ) : notes.length === 0 ? (
        <div className="pc np__empty">
          <HiOutlineCalendar className="np__empty-icon"/>
          <p className="np__empty-title">No messages yet</p>
          <p className="np__empty-sub">
            When the Forge team sends you a note about your application, it will appear here.
            You'll also receive an email notification with a link back to this page.
          </p>
        </div>
      ) : (
        <div className="np__notes-list">
          {[...notes].reverse().map(note => (
            <NoteCard key={note.id} note={note} />
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
